import React, { useEffect, Suspense, lazy } from "react";
import { useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { GridRows } from "@visx/grid";
import { Group } from "@visx/group";
import { scaleLinear, scaleOrdinal, scaleBand } from "@visx/scale";
import { LinePath, AreaClosed, Bar } from "@visx/shape";
import { LegendOrdinal, LegendItem, LegendLabel } from "@visx/legend";
import { curveMonotoneX } from "@visx/curve";
import { ParentSize } from "@visx/responsive";
import { graphql } from "~/graphql/codegen";
import { useQuery } from "@apollo/client/react";
import { isFrontend } from "~/utils";
import metrics from "~/utils/metrics";
import ChartControls from "./chart-controls";
import { UsageTab } from "./usage-tabs";
import { GlyphCircle } from "@visx/glyph";
import { useTooltip, TooltipWithBounds } from "@visx/tooltip";
import { localPoint } from "@visx/event";
import type {
  AiUsageQuery,
  AiUsageQueryVariables,
  AiUsageLastLogDateQuery,
  AiUsageLastLogDateQueryVariables,
} from "~/graphql/codegen/graphql";
import { formatDate } from "~/utils";

const AI_USAGE_QUERY = graphql(`
  query AiUsage {
    currentUser {
      aiUsage {
        timeSaved {
          monthSince
          value
          entryType
        }
        timeSpent {
          monthSince
          value
          entryType
        }
        savedVsSpent {
          monthSince
          value
          entryType
        }
      }
    }
    aiUsage {
      timeSaved {
        monthSince
        value
        entryType
      }
      timeSpent {
        monthSince
        value
        entryType
      }
      savedVsSpent {
        monthSince
        value
        entryType
      }
    }
  }
`);

const AI_USAGE_LAST_LOG_DATE_QUERY = graphql(`
  query AiUsageLastLogDate {
    currentUser {
      aiUsage {
        lastLogDate
      }
    }
  }
`);

// Type for time periods
type TimePeriod = "3 months" | "6 months" | "12 months";

// Simplified data structure used by the chart after mapping GraphQL response
interface UsageData {
  date: string;
  yourHours: number;
  avgUserHours: number;
  submissionType?: "survey" | "platform";
}

const primaryColor = "#A78BFA";
const avgUserColor = "#FBBF24";
// SVG fill that matches the current theme background (light or dark)
// eslint-disable-next-line lingui/no-unlocalized-strings -- CSS color value
const backgroundFill = "hsl(var(--background))";

const formatAxisDate = (
  value: { valueOf(): number },
  t: (descriptor: any) => string,
) => {
  const i = value.valueOf();
  if (i === 0) return t(msg`Start`);
  const suffix =
    i === 1 ? t(msg`st`) : i === 2 ? t(msg`nd`) : i === 3 ? t(msg`rd`) : t(msg`th`);
  return t(msg`${i}${suffix} month`);
};

const createTickFormatter = (
  chartType: "Line" | "Bar",
  t: (descriptor: any) => string,
) => {
  return (value: any) => {
    if (chartType === "Bar") {
      const adjustedIndex = Number(value) + 1;
      const suffix =
        adjustedIndex === 1
          ? t(msg`st`)
          : adjustedIndex === 2
            ? t(msg`nd`)
            : adjustedIndex === 3
              ? t(msg`rd`)
              : t(msg`th`);
      return t(msg`${adjustedIndex}${suffix} month`);
    }
    return formatAxisDate(value, t);
  };
};

const getOptimalTickCount = (timePeriod: TimePeriod): number => {
  switch (timePeriod) {
    case "3 months":
      return 3;
    case "6 months":
      return 6;
    case "12 months":
      return 6;
    default:
      return 6;
  }
};

const monthSinceToLabel = (
  monthSince: number,
  t: (descriptor: any) => string,
): string => {
  if (monthSince === 0) return t(msg`Start`);
  const suffix =
    monthSince === 1
      ? t(msg`st`)
      : monthSince === 2
        ? t(msg`nd`)
        : monthSince === 3
          ? t(msg`rd`)
          : t(msg`th`);
  return t(msg`${monthSince}${suffix} month`);
};

// Convert GraphQL response into UsageData[] for the selected graph and time period
const mapGraphToUsageData = (
  graph:
    | {
        mine: { monthSince: number; value: number }[];
        all: { monthSince: number; value: number }[];
      }
    | undefined,
  timePeriod: TimePeriod,
  chartType: "Line" | "Bar",
  isCumulative: boolean = false,
  t: (descriptor: any) => string = (d) => String(d),
): UsageData[] => {
  if (!graph) return [];

  const sliceCount = timePeriod === "3 months" ? 3 : timePeriod === "6 months" ? 6 : 12;
  const mine = graph.mine.slice(0, sliceCount);
  const all = graph.all.slice(0, sliceCount);

  const baseline: UsageData = {
    date: monthSinceToLabel(0, t),
    yourHours: 0,
    avgUserHours: 0,
    submissionType: "platform" as const,
  };

  if (isCumulative) {
    let cumulativeMine = 0;
    let cumulativeAvg = 0;

    const cumulativeSeries = mine.map((item, idx) => {
      cumulativeMine += item.value;
      cumulativeAvg += all[idx]?.value || 0;

      return {
        date: monthSinceToLabel(item.monthSince, t),
        yourHours: cumulativeMine,
        avgUserHours: cumulativeAvg,
        submissionType: "platform" as const,
      };
    });

    return chartType === "Line" ? [baseline, ...cumulativeSeries] : cumulativeSeries;
  }

  const series = mine.map((item, idx) => ({
    date: monthSinceToLabel(item.monthSince, t),
    yourHours: item.value,
    avgUserHours: all[idx]?.value || 0,
    submissionType: "platform" as const,
  }));

  return chartType === "Line" ? [baseline, ...series] : series;
};

function UserUsageChartInner() {
  const { t } = useLingui();
  const frontend = isFrontend();
  // eslint-disable-next-line lingui/no-unlocalized-strings -- internal state key
  const [chartType, setChartType] = React.useState<"Line" | "Bar">("Line");
  // eslint-disable-next-line lingui/no-unlocalized-strings -- internal state key
  const [timePeriod, setTimePeriod] = React.useState<TimePeriod>("3 months");
  const [isCumulative, setIsCumulative] = React.useState(true);

  const [activeTab, setActiveTab] = React.useState<UsageTab>(UsageTab.TimeSaved);

  const title =
    activeTab === UsageTab.TimeSaved
      ? t(msg`Time saved using AI`)
      : activeTab === UsageTab.TimeSpent
        ? t(msg`Time experimenting with AI`)
        : t(msg`Time saved vs time experimenting using AI`);

  const YOUR_HOURS_LABEL = t(msg`Your hours`);
  const AVG_USER_HOURS_LABEL = t(msg`Average user hours`);

  const { data: queryData } = useQuery<AiUsageQuery, AiUsageQueryVariables>(AI_USAGE_QUERY);
  const { data: lastLogData } = useQuery<AiUsageLastLogDateQuery, AiUsageLastLogDateQueryVariables>(
    AI_USAGE_LAST_LOG_DATE_QUERY,
  );

  const myGraph = queryData?.currentUser?.aiUsage;
  const userAvgGraph = queryData?.aiUsage;

  const graphs = React.useMemo(() => {
    if (!myGraph || !userAvgGraph) return undefined;
    return {
      timeSaved: {
        mine: myGraph.timeSaved ?? [],
        all: userAvgGraph.timeSaved ?? [],
      },
      timeSpent: {
        mine: myGraph.timeSpent ?? [],
        all: userAvgGraph.timeSpent ?? [],
      },
      savedVsSpent: {
        mine: myGraph.savedVsSpent ?? [],
        all: userAvgGraph.savedVsSpent ?? [],
      },
    };
  }, [myGraph, userAvgGraph]);

  const lastCheckInDate = lastLogData?.currentUser?.aiUsage?.lastLogDate
    ? formatDate(new Date(lastLogData.currentUser.aiUsage.lastLogDate))
    : undefined;

  const lastSavedHours = myGraph?.timeSaved?.length ? myGraph.timeSaved[myGraph.timeSaved.length - 1].value : undefined;

  const selectedGraph = React.useMemo(() => {
    if (!graphs) return undefined;
    switch (activeTab) {
      case UsageTab.TimeSpent:
        return graphs.timeSpent;
      case UsageTab.SavedVsSpent:
        return graphs.savedVsSpent;
      default:
        return graphs.timeSaved;
    }
  }, [graphs, activeTab]);

  const data: UsageData[] = React.useMemo(
    () => mapGraphToUsageData(selectedGraph, timePeriod, chartType, isCumulative, t),
    [selectedGraph, timePeriod, chartType, isCumulative, t],
  );
  const margin = { top: 20, right: 40, bottom: 30, left: 40 };

  // Determine dynamic Y-axis max and tick interval
  const { yAxisMax, tickStep } = React.useMemo(() => {
    const values = data.flatMap((d) => [d.yourHours, d.avgUserHours]);
    const maxVal = Math.max(0, ...values);

    const targetTicks = 5;
    const tempScale = scaleLinear<number>({ domain: [0, maxVal] });
    tempScale.nice(targetTicks);

    const ticks = tempScale.ticks(targetTicks);
    const step = ticks.length > 1 ? ticks[1] - ticks[0] : 1;
    const axisMax = ticks.length > 0 ? ticks[ticks.length - 1] : step;

    return { yAxisMax: axisMax, tickStep: step };
  }, [data]);

  // Clamp the scale so values below 0 do not render beneath the x-axis
  const createYScale = (height: number) =>
    scaleLinear<number>({
      domain: [0, yAxisMax],
      range: [height - margin.bottom, margin.top],
      clamp: true,
    });

  const yTicks = React.useMemo(() => {
    const ticksCount = Math.floor(yAxisMax / tickStep);
    return Array.from({ length: ticksCount + 1 }, (_, i) => i * tickStep);
  }, [yAxisMax, tickStep]);

  // Chart type change handler
  const handleChartTypeChange = React.useCallback(
    (newType: "Line" | "Bar") => {
      setChartType(newType);
      if (frontend) {
        metrics.track("chart_type_changed", {
          chart_type: newType.toLowerCase(),
          chart_title: title,
        });
      }
    },
    [frontend, title],
  );

  // Time period change handler
  const handleTimePeriodChange = React.useCallback(
    (newPeriod: TimePeriod) => {
      setTimePeriod(newPeriod);
      if (frontend) {
        metrics.track("time_period_changed", {
          time_period: newPeriod,
          chart_title: title,
        });
      }
    },
    [frontend, title],
  );

  // Tab change handler (Time saved / Time spent / Saved Vs Spent)
  const handleTabChange = React.useCallback((tab: UsageTab) => {
    setActiveTab(tab);
  }, []);

  // Analytics tracking
  useEffect(() => {
    if (frontend) {
      metrics.track("chart_viewed", {
        chart_type: "ai_usage",
        chart_title: title,
      });
    }
  }, [frontend, title]);

  const ordinalColorScale = scaleOrdinal({
    domain: [YOUR_HOURS_LABEL, AVG_USER_HOURS_LABEL],
    range: [primaryColor, avgUserColor],
  });

  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } = useTooltip<{
    seriesKey: "yours" | "avg";
    seriesLabel: string;
    hours: number;
    date: string;
    submissionType: string;
  }>();

  return (
    <div className="flex flex-col space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      </div>

      {/* Chart Controls */}
      <ChartControls
        activeTab={activeTab}
        chartType={chartType}
        timePeriod={timePeriod}
        isCumulative={isCumulative}
        onTabChange={handleTabChange}
        onChartTypeChange={handleChartTypeChange}
        onTimePeriodChange={handleTimePeriodChange}
        onCumulativeChange={setIsCumulative}
      />

      <div
        className="flex flex-col flex-1 space-y-3 min-h-[220px]"
        role="img"
        aria-label={t(msg`${chartType} chart showing ${title} over the last ${timePeriod}, comparing your hours to average user hours`)}
      >
        {data.length > 0 && (
          <div className="flex-1 min-h-[220px]">
            <ParentSize debounceTime={50}>
              {({ width: parentWidth, height: parentHeight }: { width: number; height: number }) => {
                const chartHeight = Math.max(220, parentHeight);
                const yScale = createYScale(chartHeight);
                // scales (recomputed on resize)
                const xScaleLinear = scaleLinear({
                  domain: [0, data.length - 1],
                  range: [margin.left, parentWidth - margin.right],
                });

                const xScaleBand = scaleBand({
                  domain: data.map((_, i) => i.toString()),
                  range: [margin.left, parentWidth - margin.right],
                  padding: 0.3,
                });

                return (
                  <div className="relative">
                    <svg
                      width={parentWidth}
                      height={chartHeight}
                      aria-labelledby="chart-title"
                      role="graphics-document"
                    >
                      <defs>
                        <linearGradient id="yourHoursGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop
                            offset="0%"
                            style={{
                              stopColor: primaryColor,
                              stopOpacity: 0.2,
                            }}
                          />
                          <stop
                            offset="100%"
                            style={{
                              stopColor: primaryColor,
                              stopOpacity: 0.1,
                            }}
                          />
                        </linearGradient>
                        <linearGradient id="avgUserGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop
                            offset="0%"
                            style={{
                              stopColor: avgUserColor,
                              stopOpacity: 0.2,
                            }}
                          />
                          <stop
                            offset="100%"
                            style={{
                              stopColor: avgUserColor,
                              stopOpacity: 0.1,
                            }}
                          />
                        </linearGradient>
                      </defs>

                      <GridRows
                        tickValues={yTicks}
                        left={margin.left}
                        lineStyle={{ strokeDasharray: "10 10" }}
                        className="stroke-muted-hover"
                        scale={yScale}
                        width={parentWidth - margin.left - margin.right}
                        top={0}
                      />

                      <Group>
                        {chartType === "Line" ? (
                          <>
                            {/* Line Chart - Area and Line for average user data */}
                            <AreaClosed
                              data={data}
                              x={(d, i) => xScaleLinear(i)}
                              y={(d) => yScale(d.avgUserHours)}
                              yScale={yScale}
                              fill={`url(#avgUserGradient)`}
                              curve={curveMonotoneX}
                            />
                            <LinePath
                              data={data}
                              x={(d, i) => xScaleLinear(i)}
                              y={(d) => yScale(d.avgUserHours)}
                              stroke={avgUserColor}
                              strokeWidth={2}
                              curve={curveMonotoneX}
                            />

                            {/* Line Chart - Area and Line for user data */}
                            <AreaClosed
                              data={data}
                              x={(d, i) => xScaleLinear(i)}
                              y={(d) => yScale(d.yourHours)}
                              yScale={yScale}
                              fill={`url(#yourHoursGradient)`}
                              curve={curveMonotoneX}
                            />
                            <LinePath
                              data={data}
                              x={(d, i) => xScaleLinear(i)}
                              y={(d) => yScale(d.yourHours)}
                              stroke={primaryColor}
                              strokeWidth={2}
                              curve={curveMonotoneX}
                            />

                            {/* Data point circles & tooltips */}
                            {data.map((d, i) => {
                              const cx = xScaleLinear(i);
                              const cyUser = yScale(d.yourHours);
                              const cyAvg = yScale(d.avgUserHours);
                              return (
                                <React.Fragment key={`points-${i}`}>
                                  <GlyphCircle
                                    left={cx}
                                    top={cyUser}
                                    r={4}
                                    fill={backgroundFill}
                                    stroke={primaryColor}
                                    strokeWidth={2}
                                    tabIndex={0}
                                    aria-label={t(msg`Your hours ${d.yourHours} on ${d.date}`)}
                                    onMouseMove={(event) => {
                                      const point = localPoint(event);
                                      if (point) {
                                        showTooltip({
                                          tooltipLeft: point.x,
                                          tooltipTop: point.y,
                                          tooltipData: {
                                            seriesKey: "yours",
                                            seriesLabel: YOUR_HOURS_LABEL,
                                            hours: d.yourHours,
                                            date: d.date,
                                            submissionType: d.submissionType ?? "platform",
                                          },
                                        });
                                        if (frontend) {
                                          metrics.track("usage_datapoint_hovered", {
                                            submission_type: d.submissionType ?? "platform",
                                            date: d.date,
                                          });
                                        }
                                      }
                                    }}
                                    onFocus={(event) => {
                                      const point = localPoint(event);
                                      if (point) {
                                        showTooltip({
                                          tooltipLeft: point.x,
                                          tooltipTop: point.y,
                                          tooltipData: {
                                            seriesKey: "yours",
                                            seriesLabel: YOUR_HOURS_LABEL,
                                            hours: d.yourHours,
                                            date: d.date,
                                            submissionType: d.submissionType ?? "platform",
                                          },
                                        });
                                      }
                                    }}
                                    onMouseLeave={hideTooltip}
                                    onBlur={hideTooltip}
                                  />
                                  <GlyphCircle
                                    left={cx}
                                    top={cyAvg}
                                    r={4}
                                    fill={backgroundFill}
                                    stroke={avgUserColor}
                                    strokeWidth={2}
                                    tabIndex={0}
                                    aria-label={t(msg`Average user hours ${Math.round(d.avgUserHours)} on ${d.date}`)}
                                    onMouseMove={(event) => {
                                      const point = localPoint(event);
                                      if (point) {
                                        showTooltip({
                                          tooltipLeft: point.x,
                                          tooltipTop: point.y,
                                          tooltipData: {
                                            seriesKey: "avg",
                                            seriesLabel: AVG_USER_HOURS_LABEL,
                                            hours: d.avgUserHours,
                                            date: d.date,
                                            submissionType: d.submissionType ?? "platform",
                                          },
                                        });
                                        if (frontend) {
                                          metrics.track("usage_datapoint_hovered", {
                                            submission_type: d.submissionType ?? "platform",
                                            date: d.date,
                                          });
                                        }
                                      }
                                    }}
                                    onFocus={(event) => {
                                      const point = localPoint(event);
                                      if (point) {
                                        showTooltip({
                                          tooltipLeft: point.x,
                                          tooltipTop: point.y,
                                          tooltipData: {
                                            seriesKey: "avg",
                                            seriesLabel: AVG_USER_HOURS_LABEL,
                                            hours: d.avgUserHours,
                                            date: d.date,
                                            submissionType: d.submissionType ?? "platform",
                                          },
                                        });
                                      }
                                    }}
                                    onMouseLeave={hideTooltip}
                                    onBlur={hideTooltip}
                                  />
                                </React.Fragment>
                              );
                            })}
                          </>
                        ) : (
                          <>
                            {/* Bar Chart - render bars for both datasets */}
                            {data.map((d, i) => {
                              const barWidth = xScaleBand.bandwidth() / 2;
                              const xPos = xScaleBand(i.toString()) || 0;

                              return (
                                <Group key={`bars-${i}`}>
                                  {/* Average user hours bar */}
                                  <Bar
                                    x={xPos}
                                    y={yScale(d.avgUserHours)}
                                    width={barWidth}
                                    height={chartHeight - margin.bottom - yScale(d.avgUserHours)}
                                    fill={avgUserColor}
                                    opacity={0.8}
                                    rx={6}
                                    ry={6}
                                  />
                                  {/* Your hours bar */}
                                  <Bar
                                    x={xPos + barWidth}
                                    y={yScale(d.yourHours)}
                                    width={barWidth}
                                    height={chartHeight - margin.bottom - yScale(d.yourHours)}
                                    fill={primaryColor}
                                    opacity={0.8}
                                    rx={6}
                                    ry={6}
                                  />
                                </Group>
                              );
                            })}
                          </>
                        )}
                      </Group>

                      <AxisBottom
                        top={chartHeight - margin.bottom}
                        scale={chartType === "Bar" ? xScaleBand : xScaleLinear}
                        tickFormat={createTickFormatter(chartType, t)}
                        hideTicks
                        hideAxisLine
                        numTicks={getOptimalTickCount(timePeriod)}
                        tickValues={
                          chartType === "Bar"
                            ? // keep previous branch exactly for bars
                              timePeriod === "3 months"
                              ? ["0", "1", "2"]
                              : timePeriod === "12 months"
                                ? data.map((_, i) => i.toString()).filter((_, i) => i % 2 === 0)
                                : undefined
                            : // NEW ➜ line-chart ticks: always include baseline (0)
                              timePeriod === "12 months"
                              ? data.map((_, i) => i).filter((_, i) => i % 2 === 0) // 0,2,4…
                              : data.map((_, i) => i) // 0..N for 3/6 months
                        }
                        tickComponent={(props) => {
                          const { fill, formattedValue, ...rest } = props;
                          return (
                            <text {...rest} className="fill-muted-foreground text-xs">
                              {formattedValue}
                            </text>
                          );
                        }}
                        tickLabelProps={{
                          fontSize: 12,
                          textAnchor: "middle",
                        }}
                      />
                      <AxisLeft
                        left={margin.left}
                        scale={yScale}
                        hideAxisLine
                        hideTicks
                        tickValues={yTicks.filter((v) => v !== 0)}
                        tickFormat={(l) => `${Number(l)}`}
                        tickComponent={(props) => {
                          const { fill, formattedValue, ...rest } = props;
                          return (
                            <text {...rest} className="fill-muted-foreground text-xs">
                              {formattedValue}
                            </text>
                          );
                        }}
                        tickLabelProps={{
                          fontSize: 12,
                          textAnchor: "middle",
                        }}
                      />
                    </svg>
                    {tooltipOpen && tooltipData && (
                      <TooltipWithBounds
                        left={tooltipLeft}
                        top={tooltipTop}
                        className="bg-popover text-popover-foreground shadow-md rounded-md px-3 py-1.5 text-xs z-50"
                      >
                        <div className="flex flex-col space-y-1">
                          <span>
                            {tooltipData.seriesLabel}:{" "}
                            {tooltipData.seriesKey === "avg"
                              ? Math.round(tooltipData.hours)
                              : tooltipData.hours}
                          </span>
                          {tooltipData.seriesKey === "yours" && tooltipData.submissionType !== "platform" && (
                            <span>{t(msg`Submitted via ${tooltipData.submissionType}`)}</span>
                          )}
                        </div>
                      </TooltipWithBounds>
                    )}
                  </div>
                );
              }}
            </ParentSize>
          </div>
        )}
        {data.length > 0 && (
          <>
            <LegendOrdinal scale={ordinalColorScale}>
              {(labels) => {
                return (
                  <div className="flex justify-center space-x-6 gap-x-6" role="list" aria-label={t(msg`Chart legend`)}>
                    {labels.map((label, i) => (
                      <LegendItem key={`legend-${i}`} className="flex items-center space-x-2" role="listitem">
                        <svg width={16} height={2} aria-hidden="true">
                          <rect fill={label.value} width={16} height={2} rx={1} />
                        </svg>
                        <LegendLabel className="font-medium text-xs text-foreground pl-1">{label.text}</LegendLabel>
                      </LegendItem>
                    ))}
                  </div>
                );
              }}
            </LegendOrdinal>
            {lastCheckInDate && lastSavedHours !== undefined && (
              <div className="text-xs text-muted-foreground text-center mt-2">
                {t(msg`Last check-in on ${lastCheckInDate}: ${lastSavedHours} h/w`)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Lazy-loaded component to reduce initial bundle size
const LazyUserUsageChart = lazy(() => Promise.resolve({ default: UserUsageChartInner }));

export default function UserUsageChart() {
  const frontend = isFrontend();

  // SSR guard - return null on server to prevent hydration issues
  if (!frontend) {
    return null;
  }

  return (
    <Suspense
      fallback={
        <div className="flex flex-col space-y-4 animate-pulse min-h-[220px]">
          <div className="h-4 bg-muted rounded-sm w-1/3" />
          <div className="flex-1 min-h-[220px] bg-muted rounded-sm" />
        </div>
      }
    >
      <LazyUserUsageChart />
    </Suspense>
  );
}
